/**
 * Trusted browsers (owner 2026-10-25, radaccess handler/mfa.go). After a
 * second factor radaccess may answer a finished sign-in with trustToken; when
 * the person keeps "Trust this browser" on, the token is kept here and sent
 * back in "trustedBrowsers" on every sign-in call, so the second factor is not
 * asked again on this browser for 7 days (Owner, Admin, Billing) or 30 (other
 * staff).
 *
 * 🔑 The token is radaccess's own signed statement; nothing here decides
 * anything. It never signs anyone in by itself -- the first factor is always
 * asked -- and a password change ends every token at once (server side).
 * Storage may be unavailable (private window): then nothing is remembered.
 */

const TOKENS_KEY = "ankhos.trustedBrowsers";
const CHOICE_KEY = "ankhos.trustBrowser";
const MAX_TOKENS = 5;
/** Fired on window whenever this browser's tokens change. */
export const TRUST_CHANGED_EVENT = "ankhos:trust-changed";

function announce() {
  try {
    window.dispatchEvent(new Event(TRUST_CHANGED_EVENT));
  } catch {
    // No window (tests): nothing listens.
  }
}

/** "dt1.<user>.<class>.<exp>.<epoch>.<mac>" -> exp in unix seconds, or 0. */
export function trustTokenExpiry(token: string): number {
  const p = token.split(".");
  if (p.length !== 6 || p[0] !== "dt1") return 0;
  const exp = Number(p[3]);
  return Number.isFinite(exp) ? exp : 0;
}

function read(): string[] {
  try {
    const raw = window.localStorage.getItem(TOKENS_KEY);
    const v: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.filter((t): t is string => typeof t === "string") : [];
  } catch {
    return [];
  }
}

/** The unexpired tokens to send, newest first. */
export function trustedBrowserTokens(now = Date.now()): string[] {
  return read().filter((t) => trustTokenExpiry(t) * 1000 > now).slice(0, MAX_TOKENS);
}

/** Keep a token radaccess just issued (newest first, at most five). */
export function rememberTrustToken(token: string, now = Date.now()): void {
  if (trustTokenExpiry(token) * 1000 <= now) return;
  const next = [token, ...trustedBrowserTokens(now).filter((t) => t !== token)].slice(0, MAX_TOKENS);
  try {
    window.localStorage.setItem(TOKENS_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable: this browser is simply not remembered.
  }
  announce();
}

/** Forget every token on this browser. */
export function forgetTrustTokens(): void {
  try {
    window.localStorage.removeItem(TOKENS_KEY);
  } catch {
    // Nothing stored.
  }
  announce();
}

/**
 * Until when this browser skips the extra sign-in step for userId (unix ms),
 * or 0. Read from the token itself: radaccess may have ended it earlier (a
 * password or second-step change elsewhere), never later.
 */
export function trustedUntil(userId: string | null, now = Date.now()): number {
  if (!userId) return 0;
  let best = 0;
  for (const t of trustedBrowserTokens(now)) {
    if (t.split(".")[1] === userId) best = Math.max(best, trustTokenExpiry(t) * 1000);
  }
  return best;
}

/** The person's "Trust this browser" choice; on unless turned off. */
export function trustThisBrowser(): boolean {
  try {
    return window.localStorage.getItem(CHOICE_KEY) !== "off";
  } catch {
    return false;
  }
}

export function setTrustThisBrowser(on: boolean): void {
  try {
    window.localStorage.setItem(CHOICE_KEY, on ? "on" : "off");
  } catch {
    // Storage unavailable.
  }
  if (!on) forgetTrustTokens();
}

/** Sign-in calls carry the tokens; other calls are left as they are. */
export function withTrustedBrowsers(path: string, payload: unknown): unknown {
  if (!path.startsWith("/auth/") || payload === null || typeof payload !== "object" || Array.isArray(payload)) return payload;
  const tokens = trustedBrowserTokens();
  return tokens.length ? { ...(payload as Record<string, unknown>), trustedBrowsers: tokens } : payload;
}

/** Keep a token from a finished sign-in, if the person wants this browser trusted. */
export function keepTrustFrom(path: string, body: Record<string, unknown>): void {
  if (!path.startsWith("/auth/") || typeof body.trustToken !== "string") return;
  if (trustThisBrowser()) rememberTrustToken(body.trustToken);
}
